import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../private/utils.js";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useIsGummy } from "../hooks/useGummyTheme.js";

const Root = DialogPrimitive.Root;

const Trigger = DialogPrimitive.Trigger;

const Portal = DialogPrimitive.Portal;
Portal.displayName = DialogPrimitive.Portal.displayName;

const Overlay = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
  ref?: React.Ref<HTMLDivElement>;
}) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "dark:bg-mono-950/30 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-white/30 backdrop-blur-sm",
      className
    )}
    {...props}
  />
);
Overlay.displayName = DialogPrimitive.Overlay.displayName;

const Content = ({
  className,
  children,
  innerClassName,
  hideCloseIcon,
  position = "top",
  ref,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  hideCloseIcon?: boolean;
  innerClassName?: string;
  position?: "center" | "top" | "bottom";
  ref?: React.Ref<HTMLDivElement>;
}) => {
  const isGummy = useIsGummy();
  return (
    <Portal>
      <Overlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        {...props}
        className={cn(
          "@container dark:text-mono-200 bg-mono-100 dark:bg-mono-950 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95  data-[state=open1]:slide-in-from-left-1/2  data-[state=open1]:slide-in-from-top-[48%] fixed left-[50%]  top-[50%] z-50 w-[95vw] max-w-2xl translate-x-[-50%] translate-y-[-50%] gap-4 p-6 duration-200 focus-within:ring-0 focus:ring-0 focus:ring-offset-0 focus-visible:ring-offset-0 md:w-full",
          isGummy
            ? cn("sm:rounded-surface-xl")
            : "dark:border-mono-700/50 border shadow-lg sm:rounded-lg",
          position === "center" &&
            "left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]",
          position === "top" &&
            "left-1/2 top-[5vh] max-h-[90vh] -translate-x-1/2 translate-y-0",
          position === "bottom" && "bottom-0 left-0",
          !isGummy && "outline-mono-300 dark:outline-mono-700",
          className
        )}
        onPointerDownOutside={(e) => {
          // e.preventDefault();
          // don't dismiss dialog when clicking inside the toast
          if (
            e.target instanceof Element &&
            e.target.closest("[data-sonner-toast]")
          ) {
            e.preventDefault();
          }
          // if you are wrapping this component like shadcn, also call the caller's onPointerDownOutside method
          props?.onPointerDownOutside?.(e);
        }}
      >
        <div
          ref={ref}
          className={cn(
            // Negative margin + positive padding lets inner shadows breathe
            // beyond the scroll container's edges before `overflow-y-auto`
            // clips them (overflow-y:auto forces overflow-x:auto too).
            " max-h-[calc(100dvh-6rem)] space-y-4 overflow-y-auto",
            innerClassName
          )}
        >
          {children}
        </div>
        <DialogPrimitive.Close
          aria-label="Close"
          className={cn(
            hideCloseIcon && "hidden",
            "absolute z-20 focus-visible:outline-hidden disabled:pointer-events-none",
            isGummy
              ? cn(
                  "-right-6 -top-6 inline-flex size-16 items-center justify-center rounded-full",
                  "bg-tile-primary text-primary-100",
                  "shadow-raised-soft",
                  "dark:from-mono-800 dark:to-mono-900 dark:text-mono-100",
                  "transition-transform duration-150",
                  "hover:-translate-y-0.5 active:scale-[0.92]",
                  "motion-reduce:transform-none motion-reduce:transition-none",
                  "focus-visible:ring-4 focus-visible:ring-primary-400/40"
                )
              : cn(
                  "@md:-right-4 @md:-top-4 @md:p-2 @md:border right-2 top-2 rounded-sm",
                  "bg-white dark:bg-mono-950 dark:border-mono-600/50",
                  "ring-offset-background focus-visible:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground",
                  "dark:ring-mono-800/50 transition-opacity hover:opacity-100 focus-visible:ring-2"
                )
          )}
        >
          <XMarkIcon className={cn(`size-10`)} />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </Portal>
  );
};

Content.displayName = DialogPrimitive.Content.displayName;

const Header = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
);
Header.displayName = "DialogHeader";

const Footer = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:gap-x-3",
      className
    )}
    {...props}
  />
);
Footer.displayName = "DialogFooter";

const Title = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
  ref?: React.Ref<HTMLHeadingElement>;
}) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
);
Title.displayName = DialogPrimitive.Title.displayName;

const Description = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & {
  ref?: React.Ref<HTMLParagraphElement>;
}) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-muted-foreground text-sm", className)}
    {...props}
  />
);
Description.displayName = DialogPrimitive.Description.displayName;

export const Dialog = {
  Root,
  Trigger,
  Content,
  Header,
  Footer,
  Title,
  Description
};
